/**
 * Hero dock main screen login form (`main_screen_variant: hero_dock`).
 * Cloned from classic — safe to tune independently for hero_dock.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { View, Text, ScrollView, Platform, Alert } from 'react-native';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { Clickable } from '../../../components/common/Clickable';
import { ButtonLoader } from '../../../components/load-main/ButtonLoader';
import { Input } from '../../../components/common/Input';
import { appTheme, useThemeColors } from '../../../shared/theme';
import { requestPasswordReset } from '../../../data/domains/profile';
import { finalizeLoginSuccess, signInWithPhonePassword, enrichBlockingSigninOtpParams, getAuthStore, persistSessionUser, setLoginJustHappened } from '../../../shared/auth';
import { getFcmDeviceTokenForAuth } from '../../../platform/notifications/fcmDeviceToken';
import { useOrganizationSettings, resolvePhoneMaxDigits } from '../../../data/domains/config';
import {
  authenticateWithBiometrics,
  hasStoredCredentials,
  isDeviceBiometryReady,
  loadStoredCredentials,
} from '../../../shared/auth';
import { useTranslation } from '../../../shared/i18n';
import { showErrorAlert, showSuccessAlert } from '../../../components/appAlerts';
import { useAsyncWithLoading } from '../../../components/ui/hooks/useAsyncWithLoading';
import { androidMainScreenAuthFormScrollSurface } from '../../../shared/auth/mainScreenAndroidFormSurface';
import { androidMainScreenIntrinsicFormRootStyle, androidMainScreenIntrinsicScrollCapStyle, isMainScreenAndroid } from '../../../shared/auth/onboardingMainScreenAndroidParity';
import { sharedStyles, createSolidPrimaryCtaLabelStyle } from '../../../shared/theme/sharedStyles';
import { createCredentialFormSheetStyles, createCredentialPrimaryCtaSurfaceStyle, CREDENTIAL_PILL_BIOMETRIC_RADIUS } from '../../../shared/theme/styles/sheets';
import { useAuthSheets } from '../../../shared/auth/authSheetContext';
import { getDevLoginPasswordInitial, getDevLoginPhoneInitial } from '../../../exDev';
import { authSheetFormSpacing, AUTH_FORM_KEYBOARD } from '../../../shared/auth';
import { useAuthInputKeyboardAvoidance } from '../../keyboard/useAuthInputKeyboardAvoidance';
import { AuthFocusTarget } from '../../keyboard/AuthFocusTarget';
import type { User } from '../../../data/domains/profile';

export type LoginSheetContentProps = {
  onDismiss: () => void;
  /** Main onboarding float: switch to register without `openRegister` (Reanimated-only path). */
  onInlineSwitchToRegister?: () => void;
  /**
   * Main onboarding only: do not stretch to fill the sheet; height follows the form.
   * `mainScreenScrollMaxHeight` caps the scroll area when content is taller than the viewport allowance.
   */
  mainScreenIntrinsic?: boolean;
  mainScreenScrollMaxHeight?: number;
  /** Reported from the root form wrapper `onLayout` (for crossfade shell height). */
  onFormLayoutHeight?: (heightPx: number) => void;
  /** MainScreen only: extra panel lift when scroll cannot clear the focused field. */
  onMainScreenKeyboardNudge?: (offset_px: number) => void;
};

export function LoginSheetContent({
  onDismiss,
  onInlineSwitchToRegister,
  mainScreenIntrinsic = false,
  mainScreenScrollMaxHeight,
  onFormLayoutHeight,
  onMainScreenKeyboardNudge,
}: LoginSheetContentProps) {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createCredentialFormSheetStyles(colors), [colors]);
  const { openRegister, openReset, openOtp } = useAuthSheets();
  const {
    settings: { allowedDigitForPhone: userPhoneAllowedDigit, dialing_code: dialing_code, country_iso_code: country_iso_from_settings },
  } = useOrganizationSettings();

  /** When initial config is not yet loaded (e.g. after logout), org `dialing_code` can be missing — match Register fallback. */
  const display_dialing_code = dialing_code != null ? String(dialing_code) : '965';
  const phone_max_digits = resolvePhoneMaxDigits(userPhoneAllowedDigit, display_dialing_code);

  const [phone, setPhone] = useState(getDevLoginPhoneInitial());
  const [password, setPassword] = useState(getDevLoginPasswordInitial());
  const [loading, runAsync] = useAsyncWithLoading();
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    checkBiometricAvailability();
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      const has_creds = await hasStoredCredentials();
      const hardware_ok = await isDeviceBiometryReady();
      setBiometricAvailable(has_creds && hardware_ok);
    } catch {
      setBiometricAvailable(false);
    }
  };

  const onPhoneInput = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    setPhone(cleaned.slice(0, phone_max_digits));
    if (loginError) {
      setLoginError(null);
    }
  };

  const handleBiometricLogin = async () => {
    await runAsync(async () => {
      const authenticated = await authenticateWithBiometrics({
        title: t('auth.biometricPromptTitle'),
        subtitle: t('auth.biometricPromptSubtitle'),
      });
      if (!authenticated) {
        Alert.alert(t('auth.biometricUnavailable'), t('auth.biometricUnavailableShort'));
        return;
      }
      const credentials = await loadStoredCredentials();
      if (!credentials) {
        Alert.alert(t('auth.biometricUnavailable'), t('auth.biometricUnavailableMessage'));
        setBiometricAvailable(false);
        return;
      }
      await handleLogin(credentials.username, credentials.password);
    }).catch((error: any) => {
      showErrorAlert(t, { message: error.message || t('auth.unexpectedError') });
    });
  };

  const completeLoginSuccess = useCallback(
    async (customer: User, loginPhone: string, loginPassword: string) => {
      const session_options = {
        defer: 'none' as const,
      };
      await finalizeLoginSuccess({
        user: customer,
        onDismiss,
        session_options,
        login_phone: loginPhone,
        login_password: loginPassword,
        offer_biometric_save: true,
        t,
      });
    },
    [onDismiss, t],
  );

  const showLoginFailure = useCallback(
    (message: string) => {
      const resolved = message.trim() || t('auth.loginFailed');
      setLoginError(resolved);
      showErrorAlert(t, { message: resolved });
    },
    [t],
  );

  const handleLogin = async (phoneOverride?: string, passwordOverride?: string) => {
    const loginPhone = phoneOverride || phone;
    const loginPassword = passwordOverride || password;

    if (!loginPhone || !loginPassword) {
      showLoginFailure(t('auth.phoneAndPasswordRequired'));
      return;
    }

    setLoginError(null);
    await runAsync(async () => {
      const device_token = await getFcmDeviceTokenForAuth();
      const r = await signInWithPhonePassword(loginPhone, loginPassword, { device_token, skipStoreUpdate: true });

      if (!r.ok) {
        showLoginFailure(r.message || t('auth.loginFailed'));
        return;
      }
      if (r.unverified) {
        await persistSessionUser(r.user);
        getAuthStore().setUser(r.user);
        setLoginJustHappened();
        onDismiss();
        openOtp(
          enrichBlockingSigninOtpParams({
            phone: loginPhone,
            countryCode: `+${display_dialing_code}`,
            redirectTo: 'MainTabs',
            loginData: { phone: loginPhone, password: loginPassword },
          }),
        );
        return;
      }
      await completeLoginSuccess(r.user, loginPhone, loginPassword);
    }).catch((error: unknown) => {
      showLoginFailure(error instanceof Error ? error.message : t('auth.unexpectedError'));
    });
  };

  const handleForgotPassword = async () => {
    if (!phone) {
      showErrorAlert(t, { message: t('auth.enterPhoneFirst') });
      return;
    }

    await runAsync(async () => {
      const response: any = await requestPasswordReset(phone);

      if (response.success) {
        showSuccessAlert(t, { message: response.message || t('auth.resetCodeSent') });
        openReset({
          phoneNo: phone,
          country_iso_code: country_iso_from_settings ?? 'KW',
          redirectTo: 'Login',
          historyRoute: 'Login',
        });
      } else {
        showErrorAlert(t, { message: response.message || t('auth.passwordResetFailed') });
      }
    }).catch((error: any) => {
      showErrorAlert(t, { message: error.message || t('auth.unexpectedError') });
    });
  };

  const navigateToSignup = () => (onInlineSwitchToRegister != null ? onInlineSwitchToRegister() : openRegister({}));

  const scroll_ref = useRef<ScrollView>(null);
  const scroll_y = useRef(0);
  const phone_block_ref = useRef<View>(null);
  const password_block_ref = useRef<View>(null);
  const { on_field_focus, on_scroll_layout, on_scroll_content_size_change, keyboard_scroll_pad } = useAuthInputKeyboardAvoidance({
    enabled: true,
    scroll_ref,
    scroll_y_ref: scroll_y,
    on_panel_offset: onMainScreenKeyboardNudge,
    clearance_px: AUTH_FORM_KEYBOARD.fieldFocusClearancePx,
  });

  const auth_primary_cta = useMemo(
    () => ({
      surface: createCredentialPrimaryCtaSurfaceStyle(colors),
      label: createSolidPrimaryCtaLabelStyle(colors),
    }),
    [colors],
  );

  const intrinsic_keyboard =
    mainScreenIntrinsic && isMainScreenAndroid()
      ? androidMainScreenIntrinsicFormRootStyle(mainScreenScrollMaxHeight)
      : mainScreenIntrinsic
        ? Platform.OS === 'web'
          ? { width: '100%' as const, alignSelf: 'stretch' as const, flex: 1, minHeight: 0, height: '100%' as const }
          : { width: '100%' as const, alignSelf: 'stretch' as const, flex: 0, minHeight: 0 }
        : null;
  /** `maxHeight` on ScrollView becomes its layout height; cap scroll area with a wrapper so the sheet shrink-wraps to content. */
  const intrinsic_scroll =
    mainScreenIntrinsic
      ? Platform.OS === 'web'
        ? { flex: 1, width: '100%' as const, height: '100%' as const }
        : { flex: 0, flexGrow: 0, flexShrink: 1 }
      : null;
  const intrinsic_scroll_content = mainScreenIntrinsic ? { flexGrow: 0 } : null;
  const intrinsic_scroll_cap =
    mainScreenIntrinsic && mainScreenScrollMaxHeight != null
      ? isMainScreenAndroid()
        ? androidMainScreenIntrinsicScrollCapStyle(mainScreenScrollMaxHeight)
        : Platform.OS === 'web'
          ? {
              maxHeight: mainScreenScrollMaxHeight,
              width: '100%' as const,
              flex: 1,
              height: '100%' as const,
              minHeight: 0,
            }
          : { maxHeight: mainScreenScrollMaxHeight, width: '100%' as const, flexShrink: 1 }
      : null;

  const base_scroll_pad_bottom = mainScreenIntrinsic ? appTheme.spacing.sm : authSheetFormSpacing.formPaddingBottom;

  const scroll_body = (
    <ScrollView
      ref={scroll_ref}
      onScroll={e => {
        scroll_y.current = e.nativeEvent.contentOffset.y;
      }}
      onLayout={on_scroll_layout}
      onContentSizeChange={(w, h) => {
        on_scroll_content_size_change(w, h);
        if (mainScreenIntrinsic && onFormLayoutHeight) {
          onFormLayoutHeight(h);
        }
      }}
      scrollEventThrottle={16}
      style={[styles.scroll, intrinsic_scroll]}
      contentContainerStyle={[
        styles.scrollContent,
        mainScreenIntrinsic ? styles.mainScreenScrollContent : null,
        mainScreenIntrinsic ? androidMainScreenAuthFormScrollSurface(colors) : null,
        intrinsic_scroll_content,
        mainScreenIntrinsic
          ? { paddingBottom: base_scroll_pad_bottom + keyboard_scroll_pad }
          : { paddingTop: appTheme.spacing.md, paddingBottom: base_scroll_pad_bottom + keyboard_scroll_pad },
      ]}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      showsVerticalScrollIndicator={mainScreenIntrinsic}
      nestedScrollEnabled={Platform.OS === 'android' && mainScreenIntrinsic}
      automaticallyAdjustKeyboardInsets={false}
      bounces={false}>
      <View style={mainScreenIntrinsic ? styles.mainScreenFields : styles.fields}>
        <AuthFocusTarget ref={phone_block_ref}>
          <Input
            label={t('auth.phoneNumber')}
            value={phone}
            onChangeText={onPhoneInput}
            placeholder={t('auth.phonePlaceholder')}
            keyboardType="phone-pad"
            maxLength={phone_max_digits}
            containerStyle={sharedStyles.inputContainerFlush}
            leftAccessory={<Text style={styles.countryCodeText}>+{display_dialing_code}</Text>}
            onFocus={() => on_field_focus(phone_block_ref)}
          />
        </AuthFocusTarget>
        <AuthFocusTarget ref={password_block_ref}>
          <Input
            label={t('auth.password')}
            value={password}
            onChangeText={text => {
              setPassword(text);
              if (loginError) {
                setLoginError(null);
              }
            }}
            placeholder={t('auth.enterPassword')}
            secureTextEntry
            containerStyle={sharedStyles.inputContainerFlush}
            onFocus={() => on_field_focus(password_block_ref)}
          />
        </AuthFocusTarget>
        {loginError ? (
          <Text style={[styles.signupText, { color: colors.danger, marginTop: appTheme.spacing.xxs }]} accessibilityLiveRegion="polite">
            {loginError}
          </Text>
        ) : null}
      </View>

      <Clickable
        useNativePressable
        testID="signin-forgot-password"
        onPress={handleForgotPassword}
        colorVariant="link"
        haptics
        hapticType="selection"
        accessibilityRole="link"
        accessibilityLabel={t('auth.forgotPassword')}
        hitSlop={appTheme.hitSlop.small}>
        <View style={mainScreenIntrinsic ? styles.mainScreenForgotActionRoot : styles.forgotActionRoot}>
          <Text style={styles.forgotText}>{t('auth.forgotPassword')}</Text>
        </View>
      </Clickable>

      <View style={{ marginTop: mainScreenIntrinsic ? authSheetFormSpacing.beforePrimaryActionsMainScreen : authSheetFormSpacing.beforePrimaryActions }}>
        <View style={mainScreenIntrinsic ? styles.mainScreenPrimaryRow : styles.primaryWithBiometricRow}>
          <Clickable
            useNativePressable
            testID="signin-continue"
            onPress={() => handleLogin()}
            disabled={loading}
            colorVariant="primary"
            haptics
            hapticType="impactLight"
            style={styles.primaryCtaRowSlot}
            contentBorderRadius={appTheme.borderRadius.lg}
            accessibilityLabel={t('auth.login')}
            accessibilityRole="button"
            accessibilityState={{ disabled: loading }}>
            <View style={[auth_primary_cta.surface, styles.primaryCtaSurfaceFill]}>
              {loading ? <ButtonLoader size={16} color={colors.onPrimary ?? colors.primaryText ?? colors.white} /> : <Text style={auth_primary_cta.label}>{t('auth.login')}</Text>}
            </View>
          </Clickable>
          <Clickable
            useNativePressable
            onPress={handleBiometricLogin}
            disabled={loading}
            colorVariant="outline"
            haptics
            hapticType="impactLight"
            contentBorderRadius={CREDENTIAL_PILL_BIOMETRIC_RADIUS}
            style={styles.biometricCtaClickable2}
            accessibilityLabel={t('auth.loginWithBiometrics')}
            accessibilityRole="button"
            accessibilityState={{ disabled: loading }}>
            <View style={styles.biometricCtaIconRoot}>
              <SvgIcon name="fingerprint" size={22} color={colors.primary} />
            </View>
          </Clickable>
        </View>
      </View>

      <View style={{ marginTop: mainScreenIntrinsic ? authSheetFormSpacing.afterPrimaryActionsMainScreen : authSheetFormSpacing.afterPrimaryActions }}>
        <View style={[mainScreenIntrinsic ? styles.signupRowMainScreen : styles.signupRowPad, styles.signupRow]}>
          <Text style={styles.signupText}>{t('auth.dontHaveAccount')}</Text>
          <Clickable useNativePressable testID="signin-create-account" onPress={navigateToSignup} colorVariant="link" haptics hapticType="selection" accessibilityRole="link" accessibilityLabel={t('auth.signUp')} hitSlop={appTheme.hitSlop.small}>
            <View style={styles.signUpActionRoot}>
              <Text style={styles.signupLink}>{t('auth.signUp')}</Text>
            </View>
          </Clickable>
        </View>
      </View>
    </ScrollView>
  );

  return <View style={[styles.keyboardView, intrinsic_keyboard]}>{intrinsic_scroll_cap != null ? <View style={intrinsic_scroll_cap}>{scroll_body}</View> : scroll_body}</View>;
}

export default LoginSheetContent;
